import React from 'react';
import { useHero } from '../lib/heroStore.js';

// The ladder from pulse-player docs/api.md ("Performance").
const LADDER = [
  ['0', 'high', '×1', 'display', 'every px', 'none', '×1', '×1', 'at rest'],
  ['1', 'medium', '×.85', '60', 'every 2 px', 'none', '×1', '×1, ≤8000', 'at rest'],
  ['2', 'low', '×.7', '30', 'every 2 px', 'over 160', 'radius ×.5', '×.6, ≤4000', 'moving too'],
  ['3', 'lower', '×.55', '24', 'every 3 px', 'over 64', 'radius ×.5', '×.35, ≤2500', 'moving too'],
  ['4', 'minimal', '×.45', '20', 'every 4 px', 'over 24', 'skipped', '×.35, ≤1500', 'moving too'],
];
const LEVELS = LADDER.map((r) => r[1]);

export default function GovernorSection() {
  const perf = useHero((s) => s.perf);
  const events = useHero((s) => s.events);
  const probe = useHero((s) => s.probe);
  const rung = perf ? perf.rung : -1;
  return (
    <section className="sec" id="governor" aria-labelledby="gov-h">
      <div className="wrap">
        <header className="sec-head">
          <p className="kicker">Performance governor</p>
          <h2 id="gov-h">A weak machine keeps a steady frame rate.</h2>
          <p className="sec-lead">
            At load, <code>Pulse.probe()</code> reads the GPU, limits, memory, cores and screen, and picks a tier.
            While playing, every drawn frame is timed: when over about 1.5 s the frame rate falls under 85% of the
            rung&rsquo;s rate, or the 90th-percentile frame overruns its budget, the player steps down one rung. After
            about 5 s with headroom it steps back up, never within 4 s of a step down, and a failed step up
            doubles the wait. Nothing changes what the document means: no layer is hidden and audio is never touched.
          </p>
        </header>
        <div className="gov-grid">
          <div className="panel panel-flush">
            <div className="tbl-scroll" tabIndex={0} role="region" aria-label="The governor's quality ladder">
              <table className="wx-table tbl ladder">
                <thead>
                  <tr><th>rung</th><th>level</th><th>pixels</th><th>draw rate</th><th>blur taps</th><th>post shed (reads/px)</th><th>instance filters</th><th>particles</th><th>bitmap cache</th></tr>
                </thead>
                <tbody>
                  {LADDER.map((r, i) => (
                    <tr key={r[0]} className={i === rung ? 'live' : ''} aria-current={i === rung ? 'true' : undefined}>
                      {r.map((c, j) => <td key={j}>{j === 1 && i === rung ? <><b>{c}</b> <span className="tag">hero now</span></> : c}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="gov-side">
            <div className="panel">
              <h3>Your device</h3>
              {probe ? (
                <dl className="kv">
                  <div><dt>tier</dt><dd><b>{probe.tier}</b></dd></div>
                  <div><dt>renderer</dt><dd>{probe.renderer || 'hidden by the browser'}</dd></div>
                  <div><dt>why</dt><dd>{probe.reasons?.length ? probe.reasons.join('; ') : 'no limiting factor found'}</dd></div>
                  <div><dt>hero now</dt><dd>{perf ? `${perf.level}, ${perf.fps ? Math.round(perf.fps) : '–'} fps` : '–'}</dd></div>
                </dl>
              ) : <p className="dim">Probed when the hero starts.</p>}
            </div>
            <div className="panel">
              <h3>Its decisions on this page</h3>
              {events.length === 0 && <p className="dim">None yet. Raise the hero&rsquo;s stress slider, switch on feedback trails, and watch this list.</p>}
              <ol className="ev-list">
                {events.slice(0, 8).map((e) => (
                  <li key={e.when + e.level}>
                    <b className={e.from != null && LEVELS.indexOf(e.level) > e.from ? 'down' : 'up'}>{e.from != null ? `${LEVELS[e.from]} → ${e.level}` : `starts at ${e.level}`}</b>
                    <span>{e.fps ? `${Math.round(e.fps)} fps · ` : ''}{e.from != null ? e.reason : 'the device tier'}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
